import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { RpsPrestadorRegistrado } from '@gestor-web/@core/api/ords/model';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';
import { LayoutActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { AgGridPaginationStatusBar, buildGridOptions, exportGridAsExcel, Page, PAGE_DEFAULT, PaginationStatus } from '@gestor-web/app/shared/components';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { Store } from '@ngrx/store';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import {
  DOMINIO_CLASSIFICACAO,
  DOMINIO_CONTRATUALIZACAO,
  DOMINIO_RELACAO_OPERADORA,
  DOMINIO_URGENCIA_EMERGENCIA,
  FILTRO_VAZIO,
  RpsPrestadoresRegistradosFilterData,
} from './components/filtro/filtro.component';
import { RpsPrestadoresRegistradosService } from './rps-prestadores-registrados.service';

const GRID_KEY = 'ANALISES.RPS_PRESTADORES_REGISTRADOS';

interface RowRpsPrestadorRegistrado extends RpsPrestadorRegistrado {
  nm_ib_rede_hospitalar: string;
  nm_id_classificacao: string;
  nm_sn_urgencia_emergencia: string;
  nm_tp_relacao_operadora: string;
  nm_tp_contratualizacao: string;
  nm_tp_disponibilidade: string;
}

const COLUMNS: ColDef[] = [
  { 
    headerName: 'Arquivo',
     field: 'cd_arquivo', 
     hide: true, 
     width: 110 
  },
  { 
    headerName: 'Linha', 
    field: 'nr_linha', 
    hide: true, 
    width: 100 
  },
  {
    headerName: 'Competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 130,
  },
  { 
    headerName: 'Rede hospitalar', 
    field: 'nm_ib_rede_hospitalar', 
    width: 150 
  },
  { 
    headerName: 'CPF/CNPJ',
     field: 'nr_cpf_cnpj',
      type: 'cpfCnpj',
       width: 170 
  },
  { 
    headerName: 'CNES',
     field: 'nr_cnes',
      width: 110 
  },
  {
    headerName: 'Nr. prestador na operadora',
    field: 'nr_prestador_ops',
    width: 200,
  },
  { headerName: 'Nome do prestador',
     field: 'nm_prestador',
    width: 300 
  },
  { 
    headerName: 'Cód. município',
     field: 'cd_municipio_semdv',
      width: 130 
  },
  { 
    headerName: 'UF',
     field: 'sg_uf',
      width: 80
  },
  { 
    headerName: 'Classificação',
     field: 'nm_id_classificacao',
      width: 220 
  },
  {
    headerName: 'Urgência/Emergência',
    field: 'nm_sn_urgencia_emergencia',
    width: 180,
  },
  {
    headerName: 'Relação com a operadora',
    field: 'nm_tp_relacao_operadora',
    width: 220,
  },
  {
    headerName: 'Contratualização',
    field: 'nm_tp_contratualizacao',
    width: 200,
  },
  { 
    headerName: 'Disponibilidade',
     field: 'nm_tp_disponibilidade',
    width: 180 
  },
  {
    headerName: 'Início do vínculo',
    field: 'dt_vinculo_inicio',
    type: 'date',
    width: 150,
  },
  {
    headerName: 'Fim do vínculo',
    field: 'dt_vinculo_fim',
    type: 'date',
    width: 150,
  },
  { 
    headerName: 'Vínculo de rede',
     field: 'cd_vinculo_rede',
    width: 150 
  },
  { 
    headerName: 'Cód. plano ANS',
     field: 'cd_plano_ans',
      width: 140 
  },
  { 
    headerName: 'Registro do plano',
     field: 'nr_registro_plano',
      width: 160 
  },
];

@Component({
  selector: 'bl-rps-prestadores-registrados',
  templateUrl: './rps-prestadores-registrados.component.html',
  styleUrls: ['./rps-prestadores-registrados.component.scss'],
  providers: [RpsPrestadoresRegistradosService, DatePipe],
  standalone: false,
})
export class RpsPrestadoresRegistradosComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<RowRpsPrestadorRegistrado>;

  gridOptions: GridOptions<RowRpsPrestadorRegistrado>;

  page: number = PAGE_DEFAULT.page;
  pageSize: number = PAGE_DEFAULT.limit;
  pageData: Page = PAGE_DEFAULT;
  paginationReset = false;
  pagination$ = new BehaviorSubject<PaginationStatus>(null);

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  rowData: RowRpsPrestadorRegistrado[];
  rowTotal = 0;

  pageTooltip = 'RELATORIOS_PERSONALIZADOS.RPS_PRESTADORES_REGISTRADOS';

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  operadora: string;
  operadoraNome: string;
  podeGerenciarVisoes = false;

  filterData: RpsPrestadoresRegistradosFilterData = { ...FILTRO_VAZIO };

  constructor(
    private permissaoService: PermissaoService,
    private service: RpsPrestadoresRegistradosService,
    private notificationService: NotificationService,
    private gridService: GridService,
    private geralService: GeralService,
    private competenciaPipe: CompetenciaPipe,
    private decimalPipe: DecimalPipe,
    private datePipe: DatePipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen).pipe(takeUntil(this.ngDestroyed$));
    this.gridHeight$ = of('100%');

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(operadora => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<RowRpsPrestadorRegistrado>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
          date: this.datePipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        statusBar: {
          statusPanels: [
            {
              statusPanel: AgGridPaginationStatusBar,
              statusPanelParams: { pagination$: this.pagination$ },
              align: 'left',
            },
          ],
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const {
      id_classificacao,
      sn_urgencia_emergencia,
      tp_relacao_operadora,
      tp_contratualizacao,
      nr_cpf_cnpj,
      nr_cnes,
      nr_registro_plano,
      visao,
      sort,
      extraFilters,
    } = params;

    const filterData: RpsPrestadoresRegistradosFilterData = {
      idClassificacao: id_classificacao ?? null,
      snUrgenciaEmergencia: sn_urgencia_emergencia ?? null,
      tpRelacaoOperadora: tp_relacao_operadora ?? null,
      tpContratualizacao: tp_contratualizacao ?? null,
      nrCpfCnpj: nr_cpf_cnpj ?? null,
      nrCnes: nr_cnes ?? null,
      nrRegistroPlano: nr_registro_plano ?? null,
    };
    this.filterData = filterData;

    const hasFilter = Object.values(filterData).some(valor => valor != null) || !!sort || !!extraFilters;

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    this.router.navigate([], { relativeTo: this.route, queryParams: {} });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    this.filterChanged = false;

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => this.agGrid?.api?.setGridOption('loading', true));
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    this.pageData = {
      page: this.page,
      size: this.pageSize,
      offset: this.page * this.pageSize,
      limit: this.pageSize,
    };

    this.service.fetchData(this.operadora, this.filterData, this.pageData).subscribe({
      next: ({ items, total }) => {
        this.rowData = items.map(item => this.toRow(item));
        this.rowTotal = total;
        this.paginationReset = false;
        this.pagination$.next({ ...this.pageData, endOffset: this.rowTotal });
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  private toRow(item: RpsPrestadorRegistrado): RowRpsPrestadorRegistrado {
    return {
      ...item,
      nm_ib_rede_hospitalar: this.geralService.buildNomeDeDominio(item.ib_rede_hospitalar, 'LOGICO_SIM_NAO', true),
      nm_id_classificacao: this.geralService.buildNomeDeDominio(item.id_classificacao, DOMINIO_CLASSIFICACAO),
      nm_sn_urgencia_emergencia: this.geralService.buildNomeDeDominio(
        item.sn_urgencia_emergencia,
        DOMINIO_URGENCIA_EMERGENCIA,
      ),
      nm_tp_relacao_operadora: this.geralService.buildNomeDeDominio(
        item.tp_relacao_operadora,
        DOMINIO_RELACAO_OPERADORA,
      ),
      nm_tp_contratualizacao: this.geralService.buildNomeDeDominio(item.tp_contratualizacao, DOMINIO_CONTRATUALIZACAO),
      nm_tp_disponibilidade: this.geralService.buildNomeDeDominio(
        item.tp_disponibilidade,
        'RPS_DISPONIBILIDADE_SERVICO',
      ),
    };
  }

  onSearch(value: string) {
    this.agGrid.api.updateGridOptions({ quickFilterText: value });
  }

  onFiltroChange(data: RpsPrestadoresRegistradosFilterData): void {
    this.filterData = data;
    this.filterChanged = true;
    this.page = 0;
    this.pageData = { ...this.pageData, page: 0, size: this.pageSize, offset: 0, limit: this.pageSize };
  }

  onPageChange(page: Page) {
    this.pageData = page;
    this.page = page.page;
    this.pageSize = page.size;

    this.reloadData();
  }

  exportExcel(): void {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const visaoNome = visao ? `- Visão ${visao?.nm_visao}` : '';

    const fileNamePrefix = `rps-prestadores-registrados-${this.operadoraNome}`;

    const headers = [
      `RPS - Prestadores registrados ${visaoNome}`,
      `${this.operadoraNome} - ${this.operadora}`,
      ...this.filtrosAplicadosTexto(),
    ];

    exportGridAsExcel({ api: this.agGrid.api, headers, fileNamePrefix });
  }

  private filtrosAplicadosTexto(): string[] {
    const { idClassificacao, snUrgenciaEmergencia, tpRelacaoOperadora, tpContratualizacao } = this.filterData;
    const { nrCpfCnpj, nrCnes, nrRegistroPlano } = this.filterData;

    return [
      idClassificacao &&
        'Classificação: ' + this.geralService.buildNomeDeDominio(idClassificacao, DOMINIO_CLASSIFICACAO),
      snUrgenciaEmergencia &&
        'Urgência/Emergência: ' +
          this.geralService.buildNomeDeDominio(snUrgenciaEmergencia, DOMINIO_URGENCIA_EMERGENCIA),
      tpRelacaoOperadora &&
        'Relação com a operadora: ' +
          this.geralService.buildNomeDeDominio(tpRelacaoOperadora, DOMINIO_RELACAO_OPERADORA),
      tpContratualizacao &&
        'Contratualização: ' + this.geralService.buildNomeDeDominio(tpContratualizacao, DOMINIO_CONTRATUALIZACAO),
      nrCpfCnpj && 'CPF/CNPJ: ' + this.cpfCnpjPipe.transform(nrCpfCnpj),
      nrCnes && 'CNES: ' + nrCnes,
      nrRegistroPlano && 'Registro do plano: ' + nrRegistroPlano,
    ].filter(Boolean);
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      id_classificacao: this.filterData.idClassificacao,
      sn_urgencia_emergencia: this.filterData.snUrgenciaEmergencia,
      tp_relacao_operadora: this.filterData.tpRelacaoOperadora,
      tp_contratualizacao: this.filterData.tpContratualizacao,
      nr_cpf_cnpj: this.filterData.nrCpfCnpj,
      nr_cnes: this.filterData.nrCnes,
      nr_registro_plano: this.filterData.nrRegistroPlano,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}
